import { AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/shared/components/form/Button";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";

import { AuditEntityHistoryModal } from "../components/AuditEntityHistoryModal";
import { AuditoriaArchivosModal } from "../components/AuditoriaArchivosModal";
import { AuditoriaFilters } from "../components/AuditoriaFilters";
import { AuditoriaPagination } from "../components/AuditoriaPagination";
import { AuditoriaTable } from "../components/AuditoriaTable";
import { PaymentAuditSection } from "../components/PaymentAuditSection";
import { VerificationRequestsSection } from "../components/VerificationRequestsSection";
import { useAuditoriaPage } from "../hooks/useAuditoriaPage";
import type { AuditoriaRow } from "../types/auditoria.types";

export const AuditoriaPage = () => {
  const { t } = useTranslations("audit");
  const { t: tGlobal } = useTranslations("global");
  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_analitica") },
      { label: tGlobal("sidebar_menu_audit"), to: "/app/audit" },
    ],
    [tGlobal],
  );
  useAppShellBreadcrumb(breadcrumb);
  const [activeTab, setActiveTab] = useState<"audit" | "payments" | "verification">("payments");
  const [archivosGalleryOpen, setArchivosGalleryOpen] = useState(false);
  const paymentControlRef = useRef<{ refetch: () => void; isLoading: boolean }>({
    refetch: () => {},
    isLoading: false,
  });
  const [paymentIsLoading, setPaymentIsLoading] = useState(false);
  const [selectedArchivosRow, setSelectedArchivosRow] = useState<AuditoriaRow | null>(null);

  const handleViewArchivos = useCallback((row: AuditoriaRow) => {
    setSelectedArchivosRow(row);
    setArchivosGalleryOpen(true);
  }, []);

  const handleCloseArchivosModal = useCallback(() => {
    setArchivosGalleryOpen(false);
    setSelectedArchivosRow(null);
  }, []);

  const {
    filters,
    setFilters,
    page,
    pageSize,
    rows,
    totalItems,
    lastPage,
    canGoPrevious,
    canGoNext,
    from,
    to,
    isLoading,
    error,
    paginationOptions,
    pageNumbers,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleItemsPerPageChange,
    handleViewHistory,
    handleCloseHistoryModal,
    historyModalOpen,
    historyEntityId,
    historyEntityType,
    refetch,
    noAccess,
    showPaymentExtendedTableColumns,
    showPayerNameInClientColumn,
  } = useAuditoriaPage();

  // The route-level `RequireAccess` gate already enforces `operations:audit:read` AND
  // `userTypes: [INTERNAL]`, both read off audit.controller.ts, so a local re-check could only
  // disagree with it. `noAccess` below stays: it is the authoritative backend 403.
  if (noAccess) {
    return <NoAccessView />;
  }

  return (
    <div className="space-y-6 p-2">
      <div className="flex items-center justify-between">
        <header>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
            {t("page_title")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t("page_subtitle")}</p>
        </header>
        {activeTab !== "verification" && (
          <Button
            variant="outline"
            onClick={activeTab === "audit" ? refetch : () => paymentControlRef.current.refetch()}
            disabled={activeTab === "audit" ? isLoading : paymentIsLoading}
            type="button"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${(activeTab === "audit" ? isLoading : paymentIsLoading) ? "animate-spin" : ""}`}
            />
            {t("button_refresh")}
          </Button>
        )}
      </div>

      <div className="border-b border-gray-200 dark:border-zinc-800">
        <nav className="-mb-px flex space-x-1" aria-label="Tabs">
          {(["payments", "audit", "verification"] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={cn(
                "px-5 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap",
                activeTab === tab
                  ? "border-green-500 text-green-600 dark:text-green-400"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-zinc-600",
              )}
            >
              {tab === "audit"
                ? t("tab_audit_general")
                : tab === "payments"
                  ? t("tab_payments")
                  : t("tab_verification")}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === "audit" ? (
        <>
          <AuditoriaFilters
            filters={filters}
            setFilters={setFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            isLoading={isLoading}
          />

          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {isLoading && rows.length === 0 ? (
            <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-12 flex flex-col items-center justify-center gap-4">
              <Loader2 className="h-10 w-10 animate-spin text-gray-400 dark:text-gray-500" />
              <p className="text-sm text-gray-500 dark:text-gray-400">{t("page_loading")}</p>
            </div>
          ) : (
            <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg overflow-hidden">
              <AuditoriaTable
                rows={rows}
                totalItems={totalItems}
                itemsPerPage={pageSize}
                paginationOptions={paginationOptions}
                onItemsPerPageChange={handleItemsPerPageChange}
                onViewArchivos={handleViewArchivos}
                onViewHistory={handleViewHistory}
                showPaymentExtendedColumns={showPaymentExtendedTableColumns}
                showPayerNameInClientColumn={showPayerNameInClientColumn}
                t={t}
              />
              <AuditoriaPagination
                currentPage={page}
                lastPage={lastPage}
                canGoPrevious={canGoPrevious}
                canGoNext={canGoNext}
                isLoading={isLoading}
                pageNumbers={pageNumbers}
                from={from}
                to={to}
                total={totalItems}
                onPageChange={handlePageChange}
                recordsText={t("pagination_records", { from, to, total: totalItems })}
                totalPagesText={lastPage != null ? t("pagination_total_pages", { lastPage }) : undefined}
                firstPageTitle={t("pagination_first_page")}
                prevPageTitle={t("pagination_prev_page")}
                nextPageTitle={t("pagination_next_page")}
                lastPageTitle={t("pagination_last_page")}
              />
            </div>
          )}

          <AuditEntityHistoryModal
            isOpen={historyModalOpen}
            onClose={handleCloseHistoryModal}
            entityType={historyEntityType}
            entityId={historyEntityId}
            filterFlujo={filters.flujo}
          />

          <AuditoriaArchivosModal
            isOpen={archivosGalleryOpen}
            onClose={handleCloseArchivosModal}
            row={selectedArchivosRow}
            alt={t("table_tooltip_view_archivos")}
          />
        </>
      ) : activeTab === "payments" ? (
        <PaymentAuditSection
          onControlChange={(ctrl) => {
            paymentControlRef.current = ctrl;
            setPaymentIsLoading(ctrl.isLoading);
          }}
        />
      ) : (
        <VerificationRequestsSection />
      )}
    </div>
  );
};
